import { Link } from "react-router-dom";
import { Logo, AironixBadge } from "../components/Logo";
import { Shield, BookOpen, Flag, Trophy, Award, Megaphone, ArrowRight, Lock, Terminal, CheckCircle2 } from "lucide-react";

const features = [
  { icon: BookOpen, title: "Structured Learning Paths", desc: "Bite-sized modules across foundations, offensive and defensive security." },
  { icon: Terminal, title: "Hands-on Quizzes", desc: "Reinforce every concept with server-scored assessments and instant feedback." },
  { icon: Flag, title: "Safe CTF Arena", desc: "Sandboxed Capture-The-Flag challenges across Web, Crypto, Forensics & OSINT." },
  { icon: Trophy, title: "XP & Leaderboards", desc: "Earn XP, level up, keep your streak and climb global rankings." },
  { icon: Award, title: "Verifiable Certificates", desc: "Complete a path, earn a certificate with public QR verification." },
  { icon: Shield, title: "Enterprise-grade Security", desc: "Role-based access, audit logging and server-authoritative rules." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0B1120] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="glass border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo className="h-9" withText />
          <div className="flex items-center gap-3">
            <Link to="/login" className="btn-ghost" data-testid="landing-login-btn">Log in</Link>
            <Link to="/register" className="btn-primary" data-testid="landing-register-btn">Get Started <ArrowRight size={16} /></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1541727261696-8680e53c1149?crop=entropy&cs=srgb&fm=jpg&q=85')", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1120]/70 via-[#0B1120]/85 to-[#0B1120]" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32">
          <div className="max-w-3xl animate-fadeup">
            <div className="overline mb-4">Learn. Practice. Protect. Excel.</div>
            <h1 className="font-heading text-4xl sm:text-6xl font-extrabold leading-[1.05] tracking-tight">
              The elite platform for
              <span className="block text-[#F26522]">cybersecurity mastery</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
              InfosecGig Academy unifies structured learning, real assessments and safe hacking challenges into one
              gamified experience — accessible anywhere, synced everywhere.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register" className="btn-primary text-base !px-7 !py-3" data-testid="hero-cta">
                Start Learning Free <ArrowRight size={18} />
              </Link>
              <Link to="/login" className="btn-secondary text-base !px-7 !py-3" data-testid="hero-login">
                <Lock size={16} /> I have an account
              </Link>
            </div>
            <div className="mt-10">
              <AironixBadge />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <span className="overline">Everything you need</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-3">One platform, end-to-end skills</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <div key={i} className="card-hover p-6 group" data-testid={`feature-${i}`}>
              <div className="h-12 w-12 rounded-lg bg-[#F26522]/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <f.icon className="text-[#F26522]" size={22} />
              </div>
              <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
              <p className="text-slate-400 text-sm mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="card p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-30" />
          <div className="relative">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold max-w-xl">Ready to become a security professional?</h2>
            <p className="text-slate-400 mt-4 max-w-lg">Join InfosecGig Academy and turn curiosity into capability. Free to start.</p>
            <ul className="mt-6 space-y-2 text-slate-300 text-sm">
              {["No credit card required", "Learn at your own pace", "Earn verifiable certificates"].map((t) => (
                <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-[#38bdf8]" /> {t}</li>
              ))}
            </ul>
            <Link to="/register" className="btn-primary text-base !px-7 !py-3 mt-8 inline-flex" data-testid="cta-register">
              Create your account <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo className="h-8" withText />
          <AironixBadge />
          <span className="text-slate-500 text-sm">© {new Date().getFullYear()} InfosecGig Academy</span>
        </div>
      </footer>
    </div>
  );
}
